import type { Operation } from '@apollo/client';
import type { ReactNode } from 'react';

import { ApolloClient, ApolloLink, ApolloProvider, createHttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { clearAuthData } from '@pesto/features';
import { toast } from '@pesto/ui/components/Toaster';

import { AUTH_BOOTSTRAP_CONTEXT_KEY, handleGraphQlError } from './apolloErrorHandling';

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('token');
  const { authorization: _previousAuthorization, ...headersWithoutAuthorization } = headers ?? {};

  return {
    headers: token ? { ...headersWithoutAuthorization, authorization: `Bearer ${token}` } : headersWithoutAuthorization,
  };
});

const path: string = import.meta.env.VITE_SC_GQL_PATH || '';

const direct_uri: string = import.meta.env.VITE_APP_URI || '';

const proxy_uri: string = import.meta.env.VITE_VGS_INBOUND_URI || '';

const directLink: ApolloLink = authLink.concat(createHttpLink({ uri: direct_uri + path }));

const proxyLink: ApolloLink = authLink.concat(createHttpLink({ uri: proxy_uri + path }));

const shouldUseProxy = (operation: Operation): boolean => {
  const sensitive: boolean = operation.getContext().containsSensitiveData;

  if (sensitive && proxy_uri) return true;

  if (sensitive)
    console.error(
      'Making PII request without setting REACT_APP_VGS_INBOUND_URI.\nSensitive fields will not be tokenized',
    );

  return false;
};

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const isPublicRoute = window.location.pathname === '/' || window.location.pathname === '/login-callback';
  const isAuthBootstrap = Boolean(operation.getContext()?.[AUTH_BOOTSTRAP_CONTEXT_KEY]);

  if (Array.isArray(graphQLErrors)) {
    for (const err of graphQLErrors) {
      const handled = handleGraphQlError({
        message: err.message,
        isAuthBootstrap,
        isPublicRoute,
        clearAuthData,
        toast,
      });

      if (handled && err.message === 'Unauthorized' && isAuthBootstrap) {
        return;
      }
    }
  }

  if (networkError) {
    toast.error('Network error. Please check your connection.');
  }
});

export const apolloClient = new ApolloClient({
  link: errorLink.concat(ApolloLink.split(shouldUseProxy, proxyLink, directLink)),
  cache: new InMemoryCache({
    typePolicies: {
      VelocityControl: {
        keyFields: ['limits'],
      },
      VelocityLimit: {
        keyFields: ['id', 'amount', 'start', 'end'],
      },
      TransactionDetailsSuperAdmin: {
        keyFields: ['entryId'],
      },
    },
  }),
});

export const ApolloProviderWrapper = (props: { children: ReactNode }) => {
  return <ApolloProvider client={apolloClient}>{props.children}</ApolloProvider>;
};
